/**
 * Entity schemas.
 *
 * The domain owns the shape; these describe how a value of that shape is recognised when it
 * arrives from outside the process. Inferring the domain types from these schemas instead would
 * invert that, and the schemas would quietly become the definition of the model.
 *
 * Keeping the two in step is enforced by `__tests__/drift.test-d.ts`, which asserts that the key
 * sets match exactly, so a field added to the domain without a rule here fails to compile.
 *
 * Annotating each schema `z.ZodType<Entity>` would be the obvious way to enforce that, and it
 * does not work: `exactOptionalPropertyTypes` is on, Zod infers an optional field as
 * `T | undefined`, and that is not assignable to `prop?: T`. The cast required to silence it
 * would also silence the check, which is worse than not claiming one.
 *
 * `.strict()` throughout: an unknown key means the sender knows about a field this build does
 * not, and accepting it would let it round-trip through storage and reappear as a shape nothing
 * validates. Rejecting is how a version mismatch becomes visible instead of subtle.
 */
import * as z from 'zod';
import { attachmentIdSchema, edgeIdSchema, folderIdSchema, mapIdSchema, nodeIdSchema, presentationIdSchema, presentationStepIdSchema, projectIdSchema, storageKeySchema, tagIdSchema, themeIdSchema, userIdSchema, workspaceIdSchema, } from './ids';
import { byteSizeSchema, finiteNumber, hexColorSchema, logicalClockSchema, millisSchema, pointSchema, schemaVersionSchema, sha256Schema, timestampSchema, viewportSchema, } from './primitives';
// ---------------------------------------------------------------------------------------------
// catalogue enums
// ---------------------------------------------------------------------------------------------
export const nodeTypeSchema = z.enum([
    'text',
    'card',
    'image',
    'video',
    'audio',
    'document',
    'table',
    'checklist',
    'radio-list',
    'location',
    'group',
    'note',
    'code',
    'formula',
]);
export const nodeShapeSchema = z.enum([
    'rectangle',
    'rounded-rectangle',
    'square',
    'circle',
    'ellipse',
    'capsule',
    'diamond',
    'hexagon',
    'octagon',
    'cloud',
    'card',
    'sticky-note',
    'balloon',
    'invisible-frame',
]);
export const nodeSideSchema = z.enum(['left', 'right', 'up', 'down', 'auto']);
export const nodeStatusSchema = z.enum([
    'none',
    'todo',
    'in-progress',
    'blocked',
    'done',
    'cancelled',
]);
export const nodePrioritySchema = z.enum(['none', 'low', 'medium', 'high', 'urgent']);
export const textAlignSchema = z.enum(['left', 'center', 'right', 'justify']);
export const verticalAlignSchema = z.enum(['top', 'middle', 'bottom']);
export const relationshipTypeSchema = z.enum([
    'hierarchy',
    'reference',
    'dependency',
    'causes',
    'blocks',
    'relates-to',
    'contradicts',
    'supports',
    'custom',
]);
export const lineTypeSchema = z.enum(['solid', 'dashed', 'dotted']);
export const routingTypeSchema = z.enum([
    'straight',
    'curved',
    'orthogonal',
    'elbow',
    'continuous',
]);
export const markerTypeSchema = z.enum(['none', 'arrow', 'open-arrow', 'circle', 'diamond', 'bar']);
export const edgeAnimationSchema = z.enum([
    'none',
    'pulse',
    'flow',
    'traveling-dot',
    'directional-glow',
    'draw-line',
]);
export const attachmentTypeSchema = z.enum([
    'image',
    'video',
    'audio',
    'document',
    'pdf',
    'spreadsheet',
    'presentation',
    'text',
    'url',
    'youtube',
    'location',
    'other',
]);
export const attachmentStatusSchema = z.enum([
    'pending',
    'uploading',
    'processing',
    'ready',
    'failed',
    'quarantined',
]);
export const presentationTargetTypeSchema = z.enum([
    'node',
    'branch',
    'group',
    'area',
    'table',
    'image',
    'video',
    'location',
    'whole-map',
]);
export const presentationTransitionSchema = z.enum([
    'none',
    'pan',
    'zoom',
    'fade',
    'focus',
    'reveal-branch',
    'collapse-siblings',
    'follow-connection',
    'morph',
]);
export const spotlightModeSchema = z.enum(['off', 'dim-others', 'hide-others', 'blur-others']);
/**
 * The layout identifier.
 *
 * A string with a shape rather than a 61-way enum: the union already exists in the type system,
 * and duplicating it here would mean two lists to keep in step. This checks the grammar; the
 * drift test is what keeps the list honest.
 */
export const layoutIdSchema = z
    .string()
    .regex(/^(?:free-form|radial|metro|timeline|matrix|board|road|layers|(?:curved-line-tree|direct-line-tree|cornered-list|cornered-tree|simple-tree-curved|simple-tree-direct|simple-list-cornered|simple-tree-cornered|continuous-tree|continuous-list|fishbone)\/(?:right|left|left-right|down|up|up-down))$/, 'unknown layout identifier');
// ---------------------------------------------------------------------------------------------
// rich text
// ---------------------------------------------------------------------------------------------
export const markSchema = z
    .object({
    kind: z.enum([
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'code',
        'highlight',
        'link',
        'mention',
    ]),
    from: z.number().int().nonnegative(),
    to: z.number().int().nonnegative(),
    href: z.string().optional(),
    targetId: z.string().optional(),
})
    .strict()
    // A mark whose end precedes its start selects nothing and breaks every renderer that assumes
    // a forward range.
    .refine((mark) => mark.to >= mark.from, 'mark end must not precede its start');
export const textSpanSchema = z
    .object({
    text: z.string(),
    marks: z.array(markSchema).optional(),
    color: z.string().optional(),
    fontSize: finiteNumber.positive().optional(),
    fontFamily: z.string().optional(),
})
    .strict();
export const richTextBlockSchema = z
    .object({
    kind: z.enum([
        'paragraph',
        'heading',
        'bullet',
        'ordered',
        'checklist',
        'radio',
        'quote',
        'code',
        'math',
    ]),
    spans: z.array(textSpanSchema),
    level: z.number().int().min(1).max(6).optional(),
    checked: z.boolean().optional(),
    language: z.string().optional(),
    align: textAlignSchema.optional(),
    indent: z.number().int().nonnegative().optional(),
})
    .strict();
export const richTextSchema = z.object({ blocks: z.array(richTextBlockSchema) }).strict();
// ---------------------------------------------------------------------------------------------
// node
// ---------------------------------------------------------------------------------------------
export const nodeStyleSchema = z
    .object({
    fill: hexColorSchema.optional(),
    stroke: hexColorSchema.optional(),
    strokeWidth: finiteNumber.nonnegative().optional(),
    strokeDash: z.array(finiteNumber.nonnegative()).optional(),
    textColor: hexColorSchema.optional(),
    fontFamily: z.string().optional(),
    fontSize: finiteNumber.positive().optional(),
    fontWeight: z.number().int().min(100).max(900).optional(),
    italic: z.boolean().optional(),
    cornerRadius: finiteNumber.nonnegative().optional(),
    opacity: finiteNumber.min(0).max(1).optional(),
    shadow: z.boolean().optional(),
    align: textAlignSchema.optional(),
    verticalAlign: verticalAlignSchema.optional(),
    paletteIndex: z.number().int().nonnegative().optional(),
})
    .strict();
/**
 * The sibling order key.
 *
 * Only the alphabet is checked, not the internal structure: the generator's encoding is its own
 * business, and pinning it here would break the day it changes. What matters is that the string
 * sorts, which any non-empty printable key does.
 */
export const orderKeySchema = z
    .string()
    .min(1, 'order key must not be empty')
    .max(256)
    .regex(/^[0-9A-Za-z]+$/, 'order key must be alphanumeric');
export const mapNodeSchema = z
    .object({
    id: nodeIdSchema,
    mapId: z.string(),
    parentId: nodeIdSchema.nullable(),
    order: orderKeySchema,
    side: nodeSideSchema,
    type: nodeTypeSchema,
    title: z.string(),
    richText: richTextSchema,
    plainText: z.string(),
    notes: richTextSchema.optional(),
    x: finiteNumber,
    y: finiteNumber,
    width: finiteNumber.nonnegative(),
    height: finiteNumber.nonnegative(),
    rotation: finiteNumber,
    zIndex: z.number().int(),
    pinned: z.boolean().optional(),
    collapsed: z.boolean(),
    locked: z.boolean(),
    hidden: z.boolean(),
    shape: nodeShapeSchema,
    layoutId: layoutIdSchema.optional(),
    style: nodeStyleSchema,
    icon: z.string().optional(),
    emoji: z.string().optional(),
    progress: finiteNumber.min(0).max(100).optional(),
    status: nodeStatusSchema.optional(),
    priority: nodePrioritySchema.optional(),
    dueDate: millisSchema.optional(),
    assignees: z.array(userIdSchema).optional(),
    tags: z.array(tagIdSchema).optional(),
    attachments: z.array(attachmentIdSchema).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    createdAt: timestampSchema,
    createdBy: userIdSchema,
    updatedAt: timestampSchema,
    updatedBy: userIdSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// edge
// ---------------------------------------------------------------------------------------------
/**
 * Shape only, with the cross-field rule left off.
 *
 * Exported because a *partial* edge update is derived from it: `.omit()` and `.partial()` cannot
 * be applied to a schema carrying a refinement, and re-listing the fields somewhere else is how
 * the two lists start disagreeing.
 */
export const edgeShapeSchema = z
    .object({
    id: edgeIdSchema,
    mapId: mapIdSchema,
    sourceNodeId: nodeIdSchema,
    targetNodeId: nodeIdSchema,
    relationshipType: relationshipTypeSchema,
    lineType: lineTypeSchema,
    routingType: routingTypeSchema,
    color: hexColorSchema.optional(),
    width: finiteNumber.positive().optional(),
    dash: z.array(finiteNumber.nonnegative()).optional(),
    startMarker: markerTypeSchema,
    endMarker: markerTypeSchema,
    label: z.string().optional(),
    animated: z.boolean(),
    animationType: edgeAnimationSchema,
    controlPoints: z.array(pointSchema).optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
})
    .strict();
// A self-link carries no information and breaks edge routing, which assumes two distinct
// endpoints to draw between.
export const edgeSchema = edgeShapeSchema.refine((edge) => edge.sourceNodeId !== edge.targetNodeId, { error: 'an edge must not connect a node to itself' });
// ---------------------------------------------------------------------------------------------
// map
// ---------------------------------------------------------------------------------------------
export const mapSettingsSchema = z
    .object({
    snapToGrid: z.boolean(),
    gridSize: finiteNumber.positive(),
    autoLayout: z.boolean(),
    preserveManualPositions: z.boolean(),
    showMinimap: z.boolean(),
    connectionAnimations: z.boolean(),
    readingDirectionArrows: z.boolean(),
})
    .strict();
export const mindMapSchema = z
    .object({
    id: mapIdSchema,
    workspaceId: workspaceIdSchema,
    projectId: projectIdSchema.nullable(),
    folderId: folderIdSchema.nullable(),
    ownerId: userIdSchema,
    title: z.string().max(500),
    description: z.string().max(5000).optional(),
    rootNodeId: nodeIdSchema,
    schemaVersion: schemaVersionSchema,
    layoutId: layoutIdSchema,
    themeId: themeIdSchema,
    viewport: viewportSchema,
    settings: mapSettingsSchema,
    localRevision: logicalClockSchema,
    serverRevision: logicalClockSchema,
    createdAt: timestampSchema,
    createdBy: userIdSchema,
    updatedAt: timestampSchema,
    updatedBy: userIdSchema,
    deletedAt: timestampSchema.nullable(),
})
    .strict()
    // On a client the server cannot have acknowledged edits this device never made. Catching it at
    // the boundary stops a swapped pair of counters from reaching the save-state derivation, where
    // it would report "synced" over unsent work.
    .refine((map) => map.serverRevision <= map.localRevision, {
    error: 'serverRevision must not exceed localRevision',
});
// ---------------------------------------------------------------------------------------------
// attachment
// ---------------------------------------------------------------------------------------------
export const attachmentSchema = z
    .object({
    id: attachmentIdSchema,
    workspaceId: workspaceIdSchema,
    mapId: mapIdSchema,
    nodeId: nodeIdSchema,
    type: attachmentTypeSchema,
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(255),
    size: byteSizeSchema,
    storageKey: storageKeySchema,
    previewKey: storageKeySchema.optional(),
    thumbnailKey: storageKeySchema.optional(),
    checksum: sha256Schema,
    status: attachmentStatusSchema,
    metadata: z.record(z.string(), z.unknown()).optional(),
    createdAt: timestampSchema,
    createdBy: userIdSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// presentation
// ---------------------------------------------------------------------------------------------
export const presentationSettingsSchema = z
    .object({
    autoplay: z.boolean(),
    stepDurationMs: millisSchema,
    loop: z.boolean(),
    showSpeakerNotes: z.boolean(),
    showProgress: z.boolean(),
    reducedMotion: z.boolean(),
})
    .strict();
export const presentationSchema = z
    .object({
    id: presentationIdSchema,
    mapId: mapIdSchema,
    title: z.string().max(500),
    description: z.string().max(5000).optional(),
    settings: presentationSettingsSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
})
    .strict();
export const presentationStepSchema = z
    .object({
    id: presentationStepIdSchema,
    presentationId: presentationIdSchema,
    order: orderKeySchema,
    title: z.string().max(500).optional(),
    targetType: presentationTargetTypeSchema,
    targetIds: z.array(nodeIdSchema),
    viewport: viewportSchema.optional(),
    zoom: finiteNumber.positive().optional(),
    transition: presentationTransitionSchema,
    durationMs: millisSchema.optional(),
    narration: z.string().optional(),
    speakerNotes: z.string().optional(),
    hiddenNodes: z.array(nodeIdSchema).optional(),
    highlightedNodes: z.array(nodeIdSchema).optional(),
    spotlightMode: spotlightModeSchema,
})
    .strict()
    // Every target type except `whole-map` needs something to point at; a step with an empty
    // target list would silently show whatever the previous step left on screen.
    .refine((step) => step.targetType === 'whole-map' || step.targetIds.length > 0, {
    error: 'a step must name at least one target unless it targets the whole map',
});
//# sourceMappingURL=entities.js.map